import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAuthoringEnvironmentInfo,
  useEntitySingleEnvironment,
  useConfigApp,
} from '@web-config-app/core-react';
import type { GetEntityDetailsPathForEnvAndId } from '@web-config-app/core-react';
import { useEntityDetailsProps } from '@web-config-app/entity-form';
import { EntityDetailsPage } from '@web-config-app/core-react-containers';
import type { DomainEntity, EnvironmentKey } from '@web-config-app/core';

interface ConfigAppEntityDetailsPageWithContextProps {
  instanceId?: string;
  domainEntity: DomainEntity;
  entityListPath: string;
  environmentKey: EnvironmentKey;
  getEntityDetailsPathForEnvAndId: GetEntityDetailsPathForEnvAndId;
}

/**
 * Requires that the `EntityDetailsProvider` is used in a parent component to provide the necessary context.
 */
export const ConfigAppEntityDetailsPageRendererWithEntityDetailsContext = ({
  instanceId,
  domainEntity,
  entityListPath,
  environmentKey,
  getEntityDetailsPathForEnvAndId,
}: ConfigAppEntityDetailsPageWithContextProps) => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  // ref to track if we've already navigated for this instance to prevent duplicates in browser history
  const hasNavigatedRef = React.useRef(false);

  const {
    status,
    formData,
    formSchema,
    formPath,
    formReady,
    onFormDataChange,
    handleNodeToggle,
    entityTree,
    operations,
    expandedNodes,
    resetEntityTreeNodes,
    setEntityName,
    currentAuthoringEnvironment,
    isReadOnly,
    entityStaticMessage,
    entity,
  } = useEntityDetailsProps();

  const { defaultLocale } = useConfigApp();

  const { publishDangerously, label: environmentName } =
    useAuthoringEnvironmentInfo(currentAuthoringEnvironment);
  const { getEntitySingleEnvironment } = useEntitySingleEnvironment();
  const { isEntitySingleEnv } = getEntitySingleEnvironment({
    domainEntity,
  });

  /**
   * This will reset the entity tree expanded state store whenever we load a new entity instance.
   */
  React.useEffect(() => {
    resetEntityTreeNodes();
    // Reset navigation flag when instanceId changes
    hasNavigatedRef.current = false;
  }, [instanceId, resetEntityTreeNodes]);

  // eslint-disable-next-line consistent-return
  React.useLayoutEffect(() => {
    /**
     * When landing on the route initially we want to set the first entity tree
     * node's id as url param in order to open and mark the first node as active
     */
    const isInstanceDataLoaded = Boolean(instanceId && operations.get.data);
    // Only navigate if we haven't already navigated for this instance - ensures this useEffect is not run more than once adding multiple entries to the history stack
    if (
      !hasNavigatedRef.current &&
      (!formPath || formPath === '') &&
      (isInstanceDataLoaded || !instanceId)
    ) {
      const frameId = requestAnimationFrame(() => {
        const [firstNode] = entityTree;
        const searchParams = new URLSearchParams({
          path: firstNode.id,
        });

        navigate(
          {
            pathname: window.location.pathname,
            search: searchParams.toString(),
          },
          // replace the current entry in the history stack so that the first node is the second in the browser stack after navigating from the list page, instead of the details page with no active node
          {
            replace: true,
            state: locationState,
          },
        );

        // set ref that we have navigated as true so this effect doesn't run again
        hasNavigatedRef.current = true;
      });

      return () => cancelAnimationFrame(frameId);
    }
  }, [
    formPath,
    entityTree,
    navigate,
    instanceId,
    operations.get.data,
    locationState,
  ]);

  const { isError, isLoading } = operations.get;

  return (
    <EntityDetailsPage
      domainEntity={domainEntity}
      entityListPath={entityListPath}
      isLoading={isLoading}
      isError={isError}
      formData={formData}
      formSchema={formSchema}
      formReady={formReady}
      formPath={formPath}
      entityTree={entityTree}
      handleNodeToggle={handleNodeToggle}
      onFormDataChange={onFormDataChange}
      expandedNodes={expandedNodes}
      publishDangerously={publishDangerously}
      entityStatus={status}
      environmentName={environmentName}
      environmentKey={environmentKey}
      isReadOnly={isReadOnly}
      getEntityDetailsPathForEnvAndId={getEntityDetailsPathForEnvAndId}
      setEntityName={setEntityName}
      isEntitySingleEnv={isEntitySingleEnv}
      entityStaticMessage={entityStaticMessage}
      defaultLocale={defaultLocale}
      localizable={entity?.localizable}
      instanceId={instanceId}
    />
  );
};
